import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Code, PenTool, Layout, ArrowRightCircle, ArrowDown, FileText, FileImage, PlayCircle, Maximize2, Volume2, VolumeX, Pause } from 'lucide-react';
import MainNavbar from '../components/MainNavbar'; // Assuming these components exist
import MainFooter from '../components/MainFooter'; // Assuming these components exist
import designxrImage1 from '../assets/designxr1_s.jpg'; // Ensure paths are correct
import designxrImage2 from '../assets/designxr2_s.jpg';
import designxrImage3 from '../assets/designxr3_s.jpg';
import HighFidelityUI from '../assets/HighFidelityUI.jpg';
import LowFidelityUI from '../assets/LowFidelityUI.jpg'; 
import UseCaseModal from '../components/UseCaseModal';
import useCaseContent from '../data/useCaseContent';
import org_benefits1 from '../assets/org_benefits1.jpg';
import org_benefits2 from '../assets/org_benefits2.jpg';
import org_benefits3 from '../assets/org_benefits3.jpg';

const Landing = () => {
  // Helper class for focus rings (adjust color if needed to match theme)
  const focusRingClass = "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-black focus:ring-white"; // Adjusted offset for dark BG
  const focusRingClassLight = "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"; // Standard offset

  // Add this state for modals
  const [activeModal, setActiveModal] = useState(null);

  // Fullscreen video modal state and refs
  const [showFullscreenVideo, setShowFullscreenVideo] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);
  const fullscreenVideoRef = useRef(null);
  
  // Effect to set page title and meta description
useEffect(() => {
  // 1. Set the page title
  document.title = 'Dessino.ai | AI-Powered UI Design and Prototyping Tool';

  // 2. Find or create the meta description tag
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    document.head.appendChild(metaDescription);
  }
  
  // 3. Set the content of the meta description
  metaDescription.content = 'Transform your ideas into interactive prototypes in minutes. Dessino.ai uses AI to generate UI designs from text, wireframes, or sketches. Get started free.';

}, []); // The empty array [] ensures this code runs only once when the page loads

  // Video control handlers
  const toggleVideoPlayback = () => {
    if (fullscreenVideoRef.current) {
      if (isVideoPlaying) {
        fullscreenVideoRef.current.pause();
      } else {
        fullscreenVideoRef.current.play();
      }
      setIsVideoPlaying(!isVideoPlaying);
    }
  };

  const toggleMute = () => {
    if (fullscreenVideoRef.current) {
      fullscreenVideoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const openFullscreenVideo = () => {
    setShowFullscreenVideo(true);
    setTimeout(() => {
      if (fullscreenVideoRef.current) {
        fullscreenVideoRef.current.play();
        setIsVideoPlaying(true);
        setIsMuted(false);
        fullscreenVideoRef.current.muted = false;
      }
    }, 100);
  };

  const closeFullscreenVideo = () => {
    setShowFullscreenVideo(false);
    setIsVideoPlaying(false);
    if (fullscreenVideoRef.current) {
      fullscreenVideoRef.current.pause();
      fullscreenVideoRef.current.currentTime = 0;
    }
  };

  return (
    // Increased overall line-height for readability
    <div className="font-sans antialiased text-gray-900 leading-relaxed">
      {/* Navbar - Using the reusable component with no active page */}
      <MainNavbar />

      {/* Hero Section with Click-to-Play Video */}
      <section className="px-4 md:px-8 py-16 md:py-24 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16 items-center">
            <div className="text-left text-center sm:text-left">
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">Transform Ideas to Prototypes <span className="block">in Minutes</span></h1>
              <p className="text-xl lg:text-2xl mb-10 opacity-90">Create interactive UI designs and prototypes using AI-powered tools to bring your vision to life.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-4 mb-4 sm:mb-8 sm:w-fit">
                {/* Mobile: View Examples in row 1, Get Started Free in row 2 */}
                {/* Desktop: Get Started Free in col 1, View Examples in col 2 */}
                <Link
                  to="/demo"
                  className="px-6 py-3 bg-white text-black rounded-lg hover:bg-gray-100 transition-colors text-center flex items-center justify-center shadow-md sm:col-start-2"
                >
                View Examples
                </Link>

                <Link
                  to="/signup"
                  className={`px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-center flex-shrink-0 flex items-center justify-center sm:col-start-1 sm:row-start-1 ${focusRingClass}`}
                >
                  Get Started Free <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
                </div>
            <p className="mt-4 sm:mt-8 text-sm opacity-75 text-center sm:text-left">No credit card required. Start with 10 free generations.</p>
            </div>
            {/* Right Side Video with Play Overlay */}
            <div className="w-full mb-6 md:mb-0 md:order-last block">
              <div className="relative w-full cursor-pointer" onClick={openFullscreenVideo}>
                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                  <video 
                    ref={videoRef}
                    autoPlay 
                    muted 
                    loop 
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover rounded-lg shadow-lg"
                  >
                    <source src="https://designxr-images.s3.us-east-1.amazonaws.com/public/HeroVideo.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  {/* Play button overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-30 rounded-lg flex items-center justify-center transition-opacity hover:bg-opacity-40">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-6 hover:bg-white/30 transition-all">
                      <PlayCircle className="w-16 h-16 text-white" />
                    </div>
                  </div>
                  {/* Expand icon */}
                  <div className="absolute top-4 right-4 bg-black/40 rounded-md p-2">
                    <Maximize2 className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fullscreen Video Modal */}
      {showFullscreenVideo && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center" onClick={closeFullscreenVideo}>
          <div className="relative w-full h-full max-w-7xl max-h-screen p-4" onClick={(e) => e.stopPropagation()}>
            <video 
              ref={fullscreenVideoRef}
              className="w-full h-full object-contain"
              controls={false}
              onEnded={() => setIsVideoPlaying(false)}
              onPlay={() => setIsVideoPlaying(true)}
              onPause={() => setIsVideoPlaying(false)}
            >
              <source src="https://designxr-images.s3.us-east-1.amazonaws.com/public/HeroVideo.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {/* Custom Video Controls */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <button onClick={toggleVideoPlayback} className="text-white hover:text-gray-300 transition-colors">
                    {isVideoPlaying ? (
                      <Pause className="w-8 h-8" />
                    ) : (
                      <PlayCircle className="w-8 h-8" />
                    )}
                  </button>
                  <button onClick={toggleMute} className="text-white hover:text-gray-300 transition-colors">
                    {isMuted ? (
                      <VolumeX className="w-6 h-6" />
                    ) : (
                      <Volume2 className="w-6 h-6" />
                    )}
                  </button>
                </div>
                <button 
                  onClick={closeFullscreenVideo}
                  className="text-white hover:text-gray-300 transition-colors text-lg font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3-Step Example - Adjusted spacing and connector alignment */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">Create Prototypes in 3 Easy Steps</h2>
          <p className="text-center text-gray-600 mb-16 max-w-2xl mx-auto">Our AI-powered platform streamlines the prototyping process, taking you from idea to interactive design in minutes.</p> {/* Increased margin */}

          {/* Ensure gap provides enough horizontal space */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 relative"> {/* Added relative positioning for lines */}
             {/* Dashed connector lines - Placed absolutely relative to the grid container */}
             {/* Note: Positioning these perfectly responsive lines purely with Tailwind can be tricky. Consider SVG or alternative methods if pixel-perfection is needed across all views. */}
             <div className="hidden md:block absolute top-8 left-1/3 right-2/3 h-0.5 border-t-2 border-dashed border-gray-300 transform -translate-x-1/2 z-0"></div>
             <div className="hidden md:block absolute top-8 left-2/3 right-1/3 h-0.5 border-t-2 border-dashed border-gray-300 transform -translate-x-1/2 z-0"></div>

            {/* Step 1 */}
            <div className="text-center z-10"> {/* Ensure content is above lines */}
              <div className="text-5xl font-bold mb-6 text-yellow-500 inline-flex items-center justify-center h-16 w-16 rounded-full bg-yellow-50 relative">1</div>
              <h3 className="text-xl font-semibold mb-3">Describe Your Idea</h3>
              <p className="text-gray-600">Express your vision in simple language, structured format, or wireframe. Our AI understands your requirements.</p>
              <div className="mt-6 flex justify-center"> {/* Increased margin */}
                <PenTool className="w-10 h-10 text-yellow-500 opacity-50" />
              </div>
            </div>

            {/* Step 2 */}
            <div className="text-center z-10">
              <div className="text-5xl font-bold mb-6 text-orange-500 inline-flex items-center justify-center h-16 w-16 rounded-full bg-orange-50 relative">2</div>
              <h3 className="text-xl font-semibold mb-3">Customize Design</h3>
              <p className="text-gray-600">Review AI-generated designs and refine them with simple revision instructions to match your exact vision.</p>
              <div className="mt-6 flex justify-center"> {/* Increased margin */}
                <Layout className="w-10 h-10 text-orange-500 opacity-50" />
              </div>
            </div>

            {/* Step 3 */}
            <div className="text-center z-10">
              <div className="text-5xl font-bold mb-6 text-red-500 inline-flex items-center justify-center h-16 w-16 rounded-full bg-red-50 relative">3</div>
              <h3 className="text-xl font-semibold mb-3">Export & Share</h3>
              <p className="text-gray-600">Download your interactive prototype as HTML or create shareable links for team collaboration and feedback.</p>
              <div className="mt-6 flex justify-center"> {/* Increased margin */}
                <Code className="w-10 h-10 text-red-500 opacity-50" />
              </div>
            </div>
          </div>
        </div>
      </section>


{/* Wireframe Upload Feature Section - Improved flow and list spacing */}
<section className="py-20 bg-white">
  <div className="max-w-6xl mx-auto px-4 sm:px-8">
    {/* Section heading - Moved to top */}
    <h2 className="text-3xl font-bold mb-10 text-center">Upload Wireframes and Get Interactive Designs</h2>
    
    {/* Visual demonstration - Responsive layout */}
    <div className="mb-12">
      <div className="flex flex-col md:flex-row items-center justify-center">
        {/* Low Fidelity UI */}
        <div className="w-full max-w-xs mb-8 md:mb-0">
          <div className="rounded-lg overflow-hidden border-2 border-gray-300">
            <img 
              src={LowFidelityUI} 
              alt="Hand-drawn wireframe of travel app" 
              className="w-full h-auto object-contain"
              width="400"
              height="600"
              loading="lazy"
            />
          </div>
        </div>
        
        {/* Arrow - Changes direction based on screen size */}
        <div className="flex justify-center py-4 md:px-8">
          <ArrowDown className="w-8 h-8 text-orange-500 block md:hidden" />
          <ArrowRight className="w-8 h-8 text-orange-500 hidden md:block" />
        </div>
        
        {/* High Fidelity UI */}
        <div className="w-full max-w-xs">
          <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm">
            <img 
              src={HighFidelityUI} 
              alt="AI-generated interactive travel app interface" 
              className="w-full h-auto object-contain"
              width="400"
              height="600"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
    
    {/* Description and features - Moved to bottom */}
    <div className="max-w-3xl mx-auto">
      <p className="text-lg text-gray-600 mb-8 text-center">
        Upload hand-drawn sketches, wireframes, or UI mockups and watch as our AI instantly transforms them into fully-functional interactive prototypes.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex items-start">
          <CheckCircle2 className="w-6 h-6 text-orange-500 mr-3 flex-shrink-0 mt-1" />
          <div>
            <p className="text-gray-800 font-medium">Instant visual prototyping</p>
            <p className="text-gray-600">Turn your static wireframes or mockups into interactive experiences</p>
          </div>
        </div>
        
        <div className="flex items-start">
          <CheckCircle2 className="w-6 h-6 text-orange-500 mr-3 flex-shrink-0 mt-1" />
          <div>
            <p className="text-gray-800 font-medium">Preserve your creative vision</p>
            <p className="text-gray-600">Our AI analyzes and implements your exact intentions</p>
          </div>
        </div>
        
        <div className="flex items-start">
          <CheckCircle2 className="w-6 h-6 text-orange-500 mr-3 flex-shrink-0 mt-1" />
          <div>
            <p className="text-gray-800 font-medium">Seamless collaboration</p>
            <p className="text-gray-600">Share links to interactive versions with stakeholders</p>
          </div>
        </div>
        
        <div className="flex items-start">
          <CheckCircle2 className="w-6 h-6 text-orange-500 mr-3 flex-shrink-0 mt-1" />
          <div>
            <p className="text-gray-800 font-medium">Refine with ease</p>
            <p className="text-gray-600">Make adjustments to the generated UI with simple text commands</p>
          </div>
        </div>
      </div>
      
      {/* CTA Button */}
      <div className="mt-12 text-center">
        <Link
          to="/signup"
          className={`px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-flex items-center ${focusRingClassLight}`}
        >
          Try Wireframe Upload <ArrowRight className="ml-2 w-4 h-4" />
        </Link>
      </div>
    </div>
  </div>
</section>



{/* View Generation Modes Section */}
<section className="py-20 bg-white">
  <div className="max-w-6xl mx-auto px-4 sm:px-8">
    <h2 className="text-3xl font-bold mb-6 text-center">Three Ways to Create Your UI</h2>
    <p className="text-lg text-gray-600 mb-12 text-center max-w-3xl mx-auto">
      Choose the creation method that works best for your workflow, from structured inputs to freeform descriptions or wireframe uploads.
    </p>
    
    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
      {/* Structured Mode */}
      <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col">
        <div className="w-16 h-16 bg-yellow-50 rounded-full flex items-center justify-center mb-6 mx-auto">
          <Layout className="w-8 h-8 text-yellow-500" />
        </div>
        <h3 className="text-xl font-semibold mb-3 text-center">Structured</h3>
        <p className="text-gray-600 mb-6 flex-grow text-center">
          Define your UI elements in a guided format with sections for layout, components, interactivity, and content.
        </p>
        <div className="space-y-3">
          <div className="flex items-start">
            <CheckCircle2 className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">Component-based approach with drag & drop sections</p>
          </div>
          <div className="flex items-start">
            <CheckCircle2 className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">Multiple layout options with customizable panels</p>
          </div>
          <div className="flex items-start">
            <CheckCircle2 className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">Perfect for precise, detailed UI requirements</p>
          </div>
        </div>
      </div>

      {/* Freestyle Mode */}
      <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col">
        <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mb-6 mx-auto">
          <FileText className="w-8 h-8 text-orange-500" />
        </div>
        <h3 className="text-xl font-semibold mb-3 text-center">Freestyle</h3>
        <p className="text-gray-600 mb-6 flex-grow text-center">
          Describe your UI vision in natural language, using free-form text to explain what you want to create.
        </p>
        <div className="space-y-3">
          <div className="flex items-start">
            <CheckCircle2 className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">Unrestricted text descriptions for maximum flexibility</p>
          </div>
          <div className="flex items-start">
            <CheckCircle2 className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">AI interprets your creative direction naturally</p>
          </div>
          <div className="flex items-start">
            <CheckCircle2 className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">Ideal for complex or unique interface concepts</p>
          </div>
        </div>
      </div>

      {/* Upload Wireframe Mode */}
      <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow flex flex-col">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-6 mx-auto">
          <FileImage className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="text-xl font-semibold mb-3 text-center">Upload Wireframe</h3>
        <p className="text-gray-600 mb-6 flex-grow text-center">
          Transform existing wireframes, sketches, or mockups into interactive UI designs with a simple upload.
        </p>
        <div className="space-y-3">
          <div className="flex items-start">
            <CheckCircle2 className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">Convert hand-drawn or digital wireframes instantly</p>
          </div>
          <div className="flex items-start">
            <CheckCircle2 className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">AI analyzes and transforms visual layouts</p>
          </div>
          <div className="flex items-start">
            <CheckCircle2 className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-gray-600">Perfect for starting with existing design assets</p>
          </div>
        </div>
      </div>
    </div>

    <div className="mt-12 text-center">
      <Link
        to="/signup"
        className={`px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-flex items-center ${focusRingClassLight}`}
      >
        Start Creating Now <ArrowRight className="ml-2 w-4 h-4" />
      </Link>
    </div>
  </div>
</section>

      {/* Benefits Section - Consistent card padding and icon alignment */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">Why Choose Us</h2>
          <p className="text-center text-gray-600 mb-16 max-w-2xl mx-auto">Our platform is designed to make prototyping accessible to everyone while saving time and enhancing creativity.</p> {/* Increased margin */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10"> {/* Adjusted gap */}
            {/* Benefit Card 1 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"> {/* Increased padding */}
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-yellow-50 p-3 rounded-lg mt-1"> {/* Added slight top margin for alignment */}
                  <CheckCircle2 className="h-6 w-6 text-yellow-500" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-semibold mb-2">Speed & Efficiency</h3>
                  <p className="text-gray-600">Reduce design time from days to minutes. Create and iterate on prototypes faster than ever with AI-powered generation.</p>
                </div>
              </div>
            </div>

            {/* Benefit Card 2 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"> {/* Increased padding */}
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-orange-50 p-3 rounded-lg mt-1"> {/* Added slight top margin */}
                  <CheckCircle2 className="h-6 w-6 text-orange-500" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-semibold mb-2">Bring your ideas to life effortlessly</h3>
                  <p className="text-gray-600">Express your ideas in plain language. Our AI handles the visual design, responsive layouts, and interactive elements for you.</p>
                </div>
              </div>
            </div>

            {/* Benefit Card 3 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"> {/* Increased padding */}
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-red-50 p-3 rounded-lg mt-1"> {/* Added slight top margin */}
                  <CheckCircle2 className="h-6 w-6 text-red-500" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-semibold mb-2">Easy Collaboration</h3>
                  <p className="text-gray-600">Share prototypes instantly with team members and stakeholders. Collect feedback and implement changes in real-time.</p>
                </div>
              </div>
            </div>

            {/* Benefit Card 4 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow"> {/* Increased padding */}
              <div className="flex items-start">
                <div className="flex-shrink-0 bg-blue-50 p-3 rounded-lg mt-1"> {/* Added slight top margin */}
                  <CheckCircle2 className="h-6 w-6 text-blue-500" />
                </div>
                <div className="ml-4">
                  <h3 className="text-xl font-semibold mb-2">Production-Ready Code</h3>
                  <p className="text-gray-600">Export clean, well-structured HTML/CSS based on your design. Seamlessly transition from prototype to development.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-12 text-center">
  <Link
    to="/signup"
    className={`px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-flex items-center ${focusRingClassLight}`}
  >
    Experience the Benefits <ArrowRight className="ml-2 w-4 h-4" />
  </Link>
</div>
      </section>

{/* Benefits for Organizations Section - Now the main use cases section */}
<section className="bg-white py-20">
  <div className="max-w-6xl mx-auto px-4 sm:px-6">
    <div className="text-center mb-16">
      <h2 className="text-3xl font-bold mb-4">Building Better Products Together</h2>
      <p className="text-lg text-gray-600 max-w-2xl mx-auto">
        Transform how your teams collaborate and bring ideas to life with tools that break down silos and accelerate development
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {/* Benefit 1: Make Design More Inclusive */}
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="aspect-square w-full mb-4 overflow-hidden">
          <img 
            src={org_benefits1}
            alt="Make Design More Inclusive"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-6 flex-grow flex flex-col">
          <h3 className="text-xl font-bold text-center mb-4">Make Design More Inclusive</h3>
          
          <p className="text-gray-600 text-center mb-6">
          Enable anyone in your organization to visually express and share product concepts.
          </p>
          
          <div className="mt-auto text-center">
            <button 
              onClick={() => setActiveModal('inclusive')} 
              className="text-orange-500 font-medium inline-flex items-center hover:text-orange-600"
            >
              Learn more <ArrowRight className="ml-1 w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Benefit 2: Enhance Interactive User Research */}
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="aspect-square w-full mb-4 overflow-hidden">
          <img 
            src={org_benefits2}
            alt="Enhance Interactive User Research"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-6 flex-grow flex flex-col">
          <h3 className="text-xl font-bold text-center mb-4">Test Realistic Interactions</h3>
          
          <p className="text-gray-600 text-center mb-6">
            Go beyond static prototypes. Gather deeper insights by testing realistic interactions before development.
          </p>
          
          <div className="mt-auto text-center">
            <button 
              onClick={() => setActiveModal('research')} 
              className="text-orange-500 font-medium inline-flex items-center hover:text-orange-600"
            >
              Learn more <ArrowRight className="ml-1 w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Benefit 3: Speed Up Development Cycles */}
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="aspect-square w-full mb-4 overflow-hidden">
          <img 
            src={org_benefits3}
            alt="Speed Up Development Cycles"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-6 flex-grow flex flex-col">
          <h3 className="text-xl font-bold text-center mb-4">Speed Up Development</h3>
          
          <p className="text-gray-600 text-center mb-6">
          Export clean, production-ready HTML and accelerate code generation by leveraging AI for fast and efficient conversion.
          </p>
          
          <div className="mt-auto text-center">
            <button 
              onClick={() => setActiveModal('development')} 
              className="text-orange-500 font-medium inline-flex items-center hover:text-orange-600"
            >
              Learn more <ArrowRight className="ml-1 w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div className="mt-12 text-center">
  <Link
    to="/signup"
    className={`px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-flex items-center ${focusRingClassLight}`}
  >
    Try for Your Enterprise <ArrowRight className="ml-2 w-4 h-4" />
  </Link>
</div>
</section>


      {/* Use Cases Section - Consistent card structure and spacing */}
      <section className="py-20 bg-gradient-to-r from-gray-50 to-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-4">For Everyone to Create Designs</h2>
          <p className="text-center text-gray-600 mb-16 max-w-2xl mx-auto">Whether you're a designer, product manager, or founder, we help you bring your vision to life</p> {/* Increased margin */}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10"> {/* Adjusted gap */}
            {/* Persona Card 1 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow text-center flex flex-col"> {/* Increased padding, added flex */}
              <div className="mb-6"> {/* Consistent margin */}
                <div className="h-24 w-24 mx-auto bg-gray-200 rounded-full overflow-hidden border-2 border-white shadow-inner"> {/* Added border/shadow */}
                  <img src={designxrImage1} alt="UX Designer" className="object-cover w-full h-full" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">UX/UI Designers</h3>
              {/* Added flex-grow and mb-6 for consistent spacing before link */}
              <p className="text-gray-600 mb-6 flex-grow">Generate multiple design options quickly and explore creative alternatives without spending hours on each mockup.</p>
              <Link
                  to="/use-cases/designers"
                  className={`text-orange-500 font-medium inline-flex items-center justify-center ${focusRingClassLight}`}
              >
                Learn more <ArrowRightCircle className="ml-1 w-4 h-4" />
              </Link>
            </div>

            {/* Persona Card 2 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow text-center flex flex-col"> {/* Increased padding, added flex */}
              <div className="mb-6"> {/* Consistent margin */}
                <div className="h-24 w-24 mx-auto bg-gray-200 rounded-full overflow-hidden border-2 border-white shadow-inner"> {/* Added border/shadow */}
                  <img src={designxrImage2} alt="Product Manager" className="object-cover w-full h-full" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Product Managers</h3>
              <p className="text-gray-600 mb-6 flex-grow">Visualize features before development begins. Validate ideas with stakeholders using interactive prototypes.</p>
              <Link
                  to="/use-cases/product-managers"
                  className={`text-orange-500 font-medium inline-flex items-center justify-center ${focusRingClassLight}`}
              >
                Learn more <ArrowRightCircle className="ml-1 w-4 h-4" />
              </Link>
            </div>

            {/* Persona Card 3 */}
            <div className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow text-center flex flex-col"> {/* Increased padding, added flex */}
              <div className="mb-6"> {/* Consistent margin */}
                <div className="h-24 w-24 mx-auto bg-gray-200 rounded-full overflow-hidden border-2 border-white shadow-inner"> {/* Added border/shadow */}
                  <img src={designxrImage3} alt="Startup Founder" className="object-cover w-full h-full" />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3">Startup Founders</h3>
              <p className="text-gray-600 mb-6 flex-grow">Create impressive prototypes for investor pitches and user testing without hiring a full design team.</p>
              <Link
                  to="/use-cases/startups"
                  className={`text-orange-500 font-medium inline-flex items-center justify-center ${focusRingClassLight}`}
              >
                Learn more <ArrowRightCircle className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* Call to Action - Emphasized spacing */}
      <section className="py-24 md:py-32 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white"> {/* Increased padding */}
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Start Creating Today</h2> {/* Increased margin */}
          <p className="text-xl mb-10 max-w-2xl mx-auto">Join others who are already transforming how they design and prototype products.</p> {/* Increased margin */}

          {/* Ensure adequate space around buttons */}
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-8"> {/* Adjusted spacing, added margin */}
            <Link
              to="/signup"
              className={`px-8 py-4 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors text-lg font-medium ${focusRingClass}`}
            >
              Get Started Free
            </Link>
            <Link
              to="/pricing"
              className={`px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors text-lg ${focusRingClass}`}
            >
              View Pricing
            </Link>
          </div>

          <p className="mt-8 text-white/80">No credit card required. 10 free generations to get you started.</p> {/* Increased margin */}
        </div>
      </section>

      

      {/* Footer - Using the reusable component */}
      <MainFooter />

      <UseCaseModal
  isOpen={activeModal !== null}
  onClose={() => setActiveModal(null)}
  modalContent={activeModal ? useCaseContent[activeModal] : { title: '', content: null }}
/>
    </div>
  );
};

export default Landing;